import { alpha } from '@mui/material/styles';
import { pagenameTextStyles } from './unifiedTheme';

/**
 * Idotoo Component Styles
 * 
 * This file contains reusable styles for components across the application.
 * Use these styles to maintain consistency in the UI.
 */

// Card Styles
export const cardStyles = {
  // Basic card with transparent white background (new default style)
  default: {
    borderRadius: 2,
    boxShadow: 'none',
    backgroundColor: (theme) => theme.palette.background.paper,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    backdropFilter: 'none',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      backgroundColor: (theme) => theme.palette.background.paper,
    },
  },
  
  // Legacy style - now renamed to 'elevated' for backward compatibility where needed
  elevated: {
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
  },
  
  // Flat card with border (now same as default)
  flat: {
    borderRadius: 2,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: 'none',
  },
  
  // Gradient background card
  gradient: {
    borderRadius: 2,
    backgroundImage: 'linear-gradient(135deg, #f6f6ff 0%, #f0f6ff 100%)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: 'none',
  },
  
  // Accent card with primary color
  accent: {
    borderRadius: 2,
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: 'none',
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
  },
  
  // Card for feature highlights
  feature: {
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '4px',
      backgroundColor: 'primary.main',
    }
  },
  
  // Dashboard section card
  dashboardSection: {
    borderRadius: 2, 
    mb: 4,
    backgroundImage: 'linear-gradient(135deg, #f6f6ff 0%, #f0f6ff 100%)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    }
  }
};

// Container styles
export const containerStyles = {
  pageContainer: {
    py: 3,
    px: { xs: 2, sm: 3 },
    maxWidth: '1200px',
    mx: 'auto',
    width: '100%',
  },
  sectionContainer: {
    mb: 4,
  }
};

// Button styles
export const buttonStyles = {
  primary: {
    borderRadius: 2,
    px: 3,
    py: 1,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    }
  },
  subtle: {
    borderRadius: 2,
    px: 2,
    textTransform: 'none',
    color: 'text.secondary',
    backgroundColor: 'background.offWhite',
    '&:hover': {
      backgroundColor: alpha('#000', 0.05),
    }
  }
};

// Text styles for consistent typography
export const textStyles = {
  pagename: pagenameTextStyles,
  pageTitle: pagenameTextStyles,
  sectionTitle: {
    fontWeight: 500,
    mb: 2,
    color: 'text.primary',
  },
  sectionDescription: {
    color: 'text.secondary',
    mb: 3,
  },
  cardTitle: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '0.9375rem', // 15px - slightly reduced
    mb: 1,
  },
  cardSubtitle: {
    color: 'text.secondary',
    fontSize: '0.875rem',
  }
};

// Common layout styles
export const layoutStyles = {
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, 1fr)',
    },
    gap: 3,
  }
}; 